import { addQueryParam, downloadBlob } from "@/lib/utils";
import type { BBox } from "@/api/q-and-a";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ColumnSort } from "@tanstack/react-table";
import { type AxiosResponse } from "axios";
import {
  apiClient,
  type ApiInfinitePaginatedResponse,
  type ApiPaginatedResponse,
  type ApiResponse,
  isApiError,
  PageParam,
  useInfiniteQuery,
} from "./_client";

// shadows Node type "DocumentType"
export const DocumentType_ = [
  { id: "project", name: "Project" },
  { id: "portfolio", name: "Portfolio" },
] as const;

export type DocumentTypeId = (typeof DocumentType_)[number]["id"];

// hack for zod
export const ZDocumentTypeId = [
  DocumentType_[0].id,
  ...DocumentType_.map((doc_type) => doc_type.id),
] as const;

export type CategorySummary = {
  summary: string;
  updated_at: string;
};
export interface DocumentCategory {
  id: string | undefined;
  name: string;
  doc_count?: number;
  drilldown?: DocumentCategory[];
  is_project?: boolean;
  children?: DocumentCategory[];
  type: DocumentTypeId;
  system_category?: boolean;
  summary?: CategorySummary;
  project_name?: string;
  portfolio_name?: string;
  drilldown_str?: string;
}

export interface BaseDocumentCategory {
  id: string | undefined;
  name: string;
  children: BaseDocumentCategory[];
  type: DocumentTypeId;
  description?: string;
  associated_field: string;
  associated_field_values: string[];
}

export interface DocumentTag {
  id: string;
  name: string;
  system_tag: boolean;
}

export type DocumentCategoryPaginatedResponse =
  ApiPaginatedResponse<DocumentCategory>;

export type DocumentCategoryInfinitePaginatedResponse =
  ApiInfinitePaginatedResponse<DocumentCategory>;

export interface Document {
  id: string;
  filename: string;
  doc_type: string;
  created_at: string;
  created_by: string;
  status: string;
  last_chat_session?: string[];
  has_embeddings?: boolean;
  parent_entity_title?: string;
  parent_entity_id?: string;
  tags?: DocumentTag[];
  instance?: { id: string; title: string };
  category_drilldown?: string;
  category?: DocumentCategory;
  category_name?: string;
  auto_categorized: boolean;
  project_id: string;
  current_task: string;
  task_embeddings_error?: string;
  drd: string;
}

export interface PartyInfo {
  name: string;
  role: string;
}

export interface DocumentInternalMetadata {
  document_category: string;
  document_type: string;
  temporal_type: string;
  reporting_period_start: string | null;
  reporting_period_end: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  data_granularity: string;
  document_title: string;
  description: string;
  status: string;
  parties: PartyInfo[];
  parent_linkage: string;
  exhibits_schedules: string[];
  extraction_confidence: number;
  updated_at: string;
}

export type DocumentPaginatedResponse = ApiPaginatedResponse<Document> & {
  results: Document[];
};

export type DocumentInfinitePaginatedResponse =
  ApiInfinitePaginatedResponse<Document>;

export const useDocumentCategories = ({
  mode = "project",
  instanceId,
  filter,
  pageSize = 500,
  ordering,
  isPrivate,
  orphanOnly,
  analysisId,
}: {
  mode?: DocumentTypeId;
  instanceId?: string;
  filter?: string;
  pageSize?: number;
  ordering?: ColumnSort;
  isPrivate?: boolean;
  orphanOnly?: boolean;
  analysisId?: string;
}) => {
  if (!filter) {
    filter = "";
  }
  const query = useInfiniteQuery<DocumentCategory>({
    queryKey: [
      "categories",
      mode,
      instanceId,
      pageSize,
      filter,
      ordering,
      isPrivate,
      orphanOnly,
      analysisId,
    ],
    queryFn: async ({ pageParam }: { pageParam: PageParam }) => {
      let url = `api/documents/categories/${mode}/`;

      url =
        typeof pageParam === "number"
          ? url + `?page_size=${pageSize}&page=${pageParam}&search=${filter}`
          : pageParam;

      if (instanceId) {
        url = addQueryParam(url, "instance_id", instanceId);
      }

      if (orphanOnly) {
        url = addQueryParam(url, "orphan_only", "true");
      }

      if (analysisId) {
        url = addQueryParam(url, "analysis_id", analysisId);
      }

      if (ordering) {
        let replacedOrdering = ordering.id;
        if (ordering.id === "filename") replacedOrdering = "name";
        url = addQueryParam(
          url,
          "ordering",
          `${ordering.desc ? "-" : ""}${replacedOrdering}`,
        );
      }

      if (isPrivate) {
        url = addQueryParam(url, "is_private", "true");
      }

      const response = await apiClient.get<
        never,
        AxiosResponse<DocumentCategoryPaginatedResponse>
      >(url);
      return response.data;
    },
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => {
      return firstPage?.previous ?? undefined;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.next ?? undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
    enabled: !!mode,
  });
  return query;
};

export const deleteCategory = async ({
  id,
  categoryType,
}: {
  id: string;
  categoryType: DocumentTypeId;
}) => {
  const response = await apiClient.delete<never, ApiResponse>(
    `api/documents/categories/${categoryType}/${id}/`,
  );
  return response;
};

export const updateCategory = async ({
  id,
  categoryType,
  name,
}: {
  id: string;
  categoryType: DocumentTypeId;
  name: string;
}) => {
  const response = await apiClient.patch<never, ApiResponse<DocumentCategory>>(
    `api/documents/categories/${categoryType}/${id}/`,
    { name },
  );
  return response;
};

export const moveCategory = async ({
  id,
  categoryType,
  instanceId,
  parentId,
}: {
  id: string;
  categoryType: DocumentTypeId;
  instanceId: string;
  parentId: string | null;
}) => {
  const response = await apiClient.post<never, ApiResponse<{ detail: string }>>(
    `api/documents/categories/${categoryType}/${id}/move/?instance_id=${instanceId}`,
    { parent_id: parentId },
  );
  return response;
};

export const useDocuments = ({
  mode,
  instanceId,
  filter,
  pageSize = 100,
  categoryId,
  filterInsideDocuments = false,
  isPrivate,
  tagIds,
}: {
  mode: DocumentTypeId;
  instanceId: string;
  filter?: string;
  pageSize?: number;
  categoryId?: string;
  filterInsideDocuments?: boolean;
  isPrivate?: boolean;
  tagIds?: string[];
}) => {
  if (!filter) {
    filter = "";
  }
  const query = useInfiniteQuery<Document>({
    queryKey: [
      "documents",
      mode,
      instanceId,
      pageSize,
      filter,
      categoryId,
      filterInsideDocuments,
      isPrivate,
      tagIds,
    ],
    refetchOnWindowFocus: false,
    queryFn: async ({ pageParam }) => {
      let url = `api/documents/categories/${mode}/documents/`;

      url =
        typeof pageParam === "number"
          ? url + `?page_size=${pageSize}&page=${pageParam}&search=${filter}`
          : pageParam;

      if (filterInsideDocuments) {
        url = addQueryParam(url, "filter_inside_documents", "true");
      }

      if (instanceId) {
        url = addQueryParam(url, "instance_id", instanceId);
      }

      if (categoryId) {
        url = addQueryParam(url, "category_id", categoryId);
      }

      if (tagIds) {
        url = addQueryParam(url, "tag_ids", tagIds.join(","));
      }

      if (isPrivate) {
        url = addQueryParam(url, "is_private", "true");
      }

      const response = await apiClient.get<
        never,
        AxiosResponse<DocumentPaginatedResponse>
      >(url);
      return response.data;
    },
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => {
      return firstPage?.previous ?? undefined;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.next ?? undefined;
    },
  });
  return query;
};

export const useTotalDocumentsCount = ({
  mode,
  instanceId,
  isPrivate,
}: {
  mode: DocumentTypeId;
  instanceId: string;
  isPrivate?: boolean;
}): UseQueryResult<{ count: number }> => {
  const query = useQuery<{ count: number }>({
    queryKey: ["documents-total-count", mode, instanceId, isPrivate],
    queryFn: async () => {
      let url = `api/documents/categories/${mode}/documents/total-count/`;
      url = addQueryParam(url, "instance_id", instanceId);
      if (isPrivate) {
        url = addQueryParam(url, "is_private", "true");
      }
      const response = await apiClient.get<
        never,
        AxiosResponse<{ count: number }>
      >(url);
      return response.data;
    },
  });
  return query;
};

export const deleteDocument = async ({
  mode,
  instanceId,
  documentId,
}: {
  mode: DocumentTypeId;
  instanceId: string;
  documentId: string;
}) => {
  const url = `api/documents/categories/${mode}/documents/${documentId}/delete/?instance_id=${instanceId}`;
  const response = await apiClient.delete<never, AxiosResponse<Document>>(url);
  return response.data;
};

export const retryDocumentIngestion = async ({
  mode,
  instanceId,
  documentId,
}: {
  mode: DocumentTypeId;
  instanceId: string;
  documentId: string;
}) => {
  const url = `api/documents/categories/${mode}/documents/${documentId}/retry-ingestion/?instance_id=${instanceId}`;
  const response = await apiClient.post<never, AxiosResponse>(url);
  return response;
};

export const bulkDeleteDocuments = async ({
  mode,
  instanceId,
  documentIds,
}: {
  mode: DocumentTypeId;
  instanceId: string;
  documentIds: string[];
}): Promise<ApiResponse<{ deleted_count: number }>> => {
  const url = `api/documents/categories/${mode}/documents/bulk-delete/?instance_id=${instanceId}`;
  const response = await apiClient.post<
    never,
    ApiResponse<{ deleted_count: number }>
  >(url, { ids: documentIds });
  return response;
};

export const downloadDocument = async ({
  mode,
  instanceId,
  documentId,
  browserDownload = true,
  forcePdf = false,
}: {
  mode: DocumentTypeId;
  instanceId: string;
  documentId: string;
  browserDownload?: boolean;
  forcePdf?: boolean;
}): Promise<{ url?: string; status?: string }> => {
  let url = `api/documents/categories/${mode}/documents/${documentId}/download/?instance_id=${instanceId}`;
  if (forcePdf) {
    url = addQueryParam(url, "force_pdf", "true");
  }
  // In local dev the endpoint streams the file itself (CORS proxy) instead of
  // returning a presigned URL, so fetch as a blob and branch on content type.
  const response = await apiClient.get<never, AxiosResponse<Blob>>(url, {
    responseType: "blob",
  });
  if (response.data.type.includes("application/json")) {
    const payload = JSON.parse(await response.data.text()) as {
      url?: string;
      status?: string;
    };
    if (payload.url && browserDownload) {
      document.location.href = payload.url;
    }
    return payload;
  }
  if (browserDownload) {
    const disposition = response.headers["content-disposition"] ?? "";
    const filename =
      /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? "document";
    downloadBlob(response.data, filename);
  }
  return {};
};

export const getOneDocument = async ({
  mode,
  instanceId,
}: {
  mode: DocumentTypeId;
  instanceId: string;
  filter?: string;
  pageSize?: number;
  ordering?: ColumnSort;
}) => {
  const url = `api/documents/categories/${mode}/documents/${instanceId}/`;
  const response = await apiClient.get<never, AxiosResponse<Document>>(url);
  return response.data;
};

export const moveDocumentToCategory = async (
  mode: DocumentTypeId,
  instanceId: string | string[],
  categoryId?: string,
) => {
  const response = await apiClient.patch<never, ApiResponse>(
    `/api/documents/categories/${mode}/documents/${instanceId}/move/`,
    {
      doc_category: categoryId,
    },
  );
  return response;
};

export const exportDocuments = async (
  mode = "project",
  instanceId: string,
  categoryId: string | undefined = undefined,
  isPrivate: boolean = false,
  browserDownload: boolean = true,
  documentIds: string[] | undefined = undefined,
  categoryIds: string[] | undefined = undefined,
) => {
  let url = `/api/documents/categories/${mode}/documents/download-zip/?instance_id=${instanceId}`;
  if (categoryIds && categoryIds.length > 0) {
    url = addQueryParam(url, "category_ids", categoryIds.join(","));
  } else if (categoryId) {
    url = addQueryParam(url, "category_id", categoryId);
  }
  if (isPrivate) {
    url = addQueryParam(url, "is_private", "true");
  }
  if (documentIds && documentIds.length > 0) {
    url = addQueryParam(url, "document_ids", documentIds.join(","));
  }
  const response = await apiClient.get<never, ApiResponse<{ url: string }>>(
    url,
  );
  if (!isApiError(response) && browserDownload) {
    document.location.href = response.data.url;
  }
  return response;
};

export const useDocumentTags = ({
  filter,
  pageSize = 100,
}: {
  filter?: string;
  pageSize?: number;
}) => {
  if (!filter) {
    filter = "";
  }
  const query = useInfiniteQuery<DocumentTag>({
    queryKey: ["tags", pageSize, filter],
    queryFn: async ({ pageParam }) => {
      let url = `api/documents/tags/`;

      url =
        typeof pageParam === "number"
          ? url + `?page_size=${pageSize}&page=${pageParam}&search=${filter}`
          : pageParam;

      const response = await apiClient.get<
        never,
        AxiosResponse<ApiPaginatedResponse<DocumentTag>>
      >(url);
      return response.data;
    },
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => {
      return firstPage?.previous ?? undefined;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.next ?? undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });
  return query;
};

export const createCategory = async (data: {
  type: string;
  name: string;
  parentId?: string;
  instance_id: string;
}) => {
  const url = `api/documents/categories/`;
  const response = await apiClient.post<never, ApiResponse<DocumentCategory>>(
    url,
    data,
  );
  return response;
};

export const createTag = async (data: {
  name: string;
  document_id?: string;
}) => {
  const url = `api/documents/tags/`;
  const response = await apiClient.post<never, ApiResponse<DocumentTag>>(
    url,
    data,
  );
  return response;
};

export const assignTag = async (data: {
  documentIds: string[];
  tagId: string;
}) => {
  const response = await apiClient.post<never, ApiResponse>(
    `api/documents/tags/${data.tagId}/add-to-document/`,
    data,
  );
  return response;
};

export const patchTag = async (data: DocumentTag) => {
  const url = `api/documents/tags/${data.id}/`;
  const response = await apiClient.patch<never, ApiResponse<DocumentTag>>(
    url,
    data,
  );
  return response;
};

export const deleteTag = async (tagId: string) => {
  const url = `api/documents/tags/${tagId}/`;
  const response = await apiClient.delete<never, ApiResponse>(url);
  return response;
};

export const getLinkToken = async ({
  documentId,
  portfolioId,
  projectId,
}: {
  documentId: string;
  portfolioId?: string;
  projectId?: string;
}) => {
  let url = `api/documents/${documentId}/link/token/`;
  if (portfolioId) {
    url = addQueryParam(url, "portfolio_id", portfolioId);
  }
  if (projectId) {
    url = addQueryParam(url, "project_id", projectId);
  }
  const response = await apiClient.get<never, ApiResponse<{ token: string }>>(
    url,
  );
  return response;
};

export const decodeLinkToken = async ({ token }: { token: string }) => {
  const response = await apiClient.get<never, ApiResponse<{ url: string }>>(
    `api/documents/link/decode/?token=${token}`,
  );
  return response;
};
export type MinimalCategory = Pick<
  DocumentCategory,
  "id" | "name" | "doc_count" | "summary"
>;

export type MinimalNestedCategory = MinimalCategory & {
  depth: number;
};

export const summarizeCategory = async (
  categoryId: string,
  categoryType: DocumentTypeId,
  instanceId: string,
  additional?: string,
) => {
  const response = await apiClient.post<never, ApiResponse<CategorySummary>>(
    `api/documents/categories/${categoryType}/${categoryId}/summarize/?instance_id=${instanceId}`,
    { additional },
  );
  return response;
};

export const downloadDocumentForFileViewer = async (
  documentId: string,
  browserDownload: boolean = true,
  forcePdf: boolean = false,
) => {
  let url = `api/documents/${documentId}/download/`;

  if (forcePdf) {
    url = addQueryParam(url, "force_pdf", "true");
  }

  const response = await apiClient.get(url, { responseType: "blob" });

  // Check if response is JSON (presigned URL) or blob (local proxy)
  const contentType = response.headers["content-type"];
  if (contentType?.includes("application/json")) {
    // Parse JSON from blob
    const text = await response.data.text();
    const jsonData = JSON.parse(text);
    if (jsonData?.url && browserDownload) {
      document.location.href = jsonData.url;
    }
    return jsonData;
  } else {
    // Direct blob response (local environment)
    const blobUrl = URL.createObjectURL(response.data);
    if (browserDownload) {
      document.location.href = blobUrl;
    }
    return { url: blobUrl };
  }
};

export const useDocumentBaseCategories = ({
  mode,
  includeChildren = false,
}: {
  mode: DocumentTypeId;
  includeChildren?: boolean;
}) => {
  const query = useQuery<BaseDocumentCategory[]>({
    queryKey: ["base-categories", mode, includeChildren],
    queryFn: async () => {
      const url = `api/documents/base-categories/${mode}/${includeChildren ? "?include_children=true" : ""}`;
      const response = await apiClient.get<
        never,
        AxiosResponse<ApiPaginatedResponse<BaseDocumentCategory>>
      >(url);
      return response.data.results;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });
  return query;
};

export const deleteDocumentBaseCategory = async (
  categoryId: string,
  mode: DocumentTypeId,
) => {
  const url = `api/documents/base-categories/${mode}/${categoryId}/`;
  const response = await apiClient.delete<never, ApiResponse>(url);
  return response;
};

export const createDocumentBaseCategory = async (
  data: {
    name: string;
    parentId?: string;
  },
  mode: DocumentTypeId,
) => {
  const url = `api/documents/base-categories/${mode}/`;
  const response = await apiClient.post<never, ApiResponse<DocumentCategory>>(
    url,
    data,
  );
  return response;
};

export const updateDocumentBaseCategory = async (
  data: {
    id: string;
    name: string;
    parent_id?: string;
    description?: string | null;
  },
  mode: DocumentTypeId,
) => {
  const url = `api/documents/base-categories/${mode}/${data.id}/`;
  const response = await apiClient.patch<never, ApiResponse<DocumentCategory>>(
    url,
    data,
  );
  return response;
};

type BulkBaseCategoriesCreateResponse = ApiResponse<
  {
    created_categories: BaseDocumentCategory[];
    updated_categories: BaseDocumentCategory[];
    skip_count: number;
  },
  { bulkfile: string }
>;

export const bulkUploadBaseCategories = async ({
  file,
  mode,
}: {
  file: File;
  mode: DocumentTypeId;
}): Promise<BulkBaseCategoriesCreateResponse> => {
  const form = new FormData();
  form.append("bulkfile", file);
  const options = {
    method: "POST",
    url: `api/documents/base-categories/${mode}/bulk/`,
    headers: { "Content-Type": "multipart/form-data" },
    data: form,
  };
  const response = await apiClient.request<
    never,
    BulkBaseCategoriesCreateResponse
  >(options);
  return response;
};

export const downloadCurrentFolderTemplate = async (mode: DocumentTypeId) => {
  const url = `api/documents/base-categories/${mode}/export/`;
  const response = await apiClient.get(url, {
    responseType: "blob",
  });

  if (response.data) {
    downloadBlob(
      response.data,
      `${mode}_categories.xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
  }

  return response;
};

export interface CitationBboxResponse {
  bboxes: BBox[];
  matched: boolean;
}

export interface CitationSignerHints {
  name?: string;
  role?: string;
  company?: string;
}

export const getCitationBboxes = async (
  documentId: string,
  page: number,
  text: string,
  signerHints?: CitationSignerHints,
): Promise<CitationBboxResponse> => {
  let url = `api/documents/${documentId}/citation-bboxes/`;
  url = addQueryParam(url, "page", page.toString());
  url = addQueryParam(url, "text", text);
  if (signerHints?.name) url = addQueryParam(url, "signer_name", signerHints.name);
  if (signerHints?.role) url = addQueryParam(url, "signer_role", signerHints.role);
  if (signerHints?.company) url = addQueryParam(url, "signer_company", signerHints.company);
  const response = await apiClient.get<never, AxiosResponse<CitationBboxResponse>>(url);
  return response.data;
};

export const useDocumentInternalMetadata = (
  documentId: string,
  enabled: boolean,
): UseQueryResult<
  AxiosResponse<DocumentInternalMetadata | { status: string }>
> =>
  useQuery({
    queryKey: ["document-internal-metadata", documentId],
    queryFn: () =>
      apiClient.get<DocumentInternalMetadata | { status: string }>(
        `api/documents/${documentId}/internal-metadata/`,
      ),
    enabled,
  });

export interface DocumentSignature {
  id: string;
  signed: boolean;
  signer_name: string;
  raw_signer_name: string;
  signer_role: string;
  signer_company: string;
  confidence: number;
  page: number | null;
  method: string;
  extracted_at: string;
}

export const useDocumentSignatures = (
  documentId: string,
  enabled: boolean,
): UseQueryResult<AxiosResponse<DocumentSignature[]>> =>
  useQuery({
    queryKey: ["document-signatures", documentId],
    queryFn: () =>
      apiClient.get<DocumentSignature[]>(
        `api/documents/${documentId}/signatures/`,
      ),
    enabled,
  });
